import { useState } from 'react';
import { Counter, Pill } from './UI';
import { Icon } from './Icons';

const TABS = ['Holidays', 'Flights', 'Hotels', 'Visa', 'Corporate'];
const CATEGORIES = ['India', 'International', 'Luxury', 'Adventure', 'Beach', 'Short Break', 'Family'];

const formatDate = (dateStr) => {
  if (!dateStr) return 'Not Specified';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

const InputWrapper = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: '9px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--brand)', marginBottom: '5px', textTransform: 'uppercase' }}>{label}</label>
    {children}
  </div>
);

const inputStyle = { width: '100%', padding: '10px 13px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', color: '#0d1117', background: '#f8fafc', boxSizing: 'border-box' };

export default function SearchWidget() {
  const [activeTab, setActiveTab] = useState('Holidays');

  // Shared / Common states
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [date, setDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  // Holidays
  const [category, setCategory] = useState('');

  // Flights
  const [flightClass, setFlightClass] = useState('Economy');

  // Hotels
  const [rooms, setRooms] = useState(1);

  // Visa
  const [visaType, setVisaType] = useState('Tourist');

  // Corporate
  const [company, setCompany] = useState('');
  const [contactName, setContactName] = useState('');
  const [passengers, setPassengers] = useState('');

  const isGroup = adults > 1;
  const isMice = adults + children > 10;

  const handleSearch = () => {
    const formattedDate = formatDate(date);
    const formattedReturnDate = formatDate(returnDate);

    let text = `🌟 *Dira Holidays - New Enquiry* 🌟\n`;
    text += `----------------------------------\n`;

    if (activeTab === 'Holidays') {
      text += `✈️ *Service Type:* Holidays\n`;
      text += `📍 *From:* ${from || 'Not Specified'}\n`;
      text += `🗺️ *To:* ${to || 'Not Specified'}\n`;
      text += `📅 *Travel Date:* ${formattedDate}\n`;
      text += `🏷️ *Category:* ${category || 'Not Specified'}\n`;
      text += `👥 *Travellers:*\n`;
      text += `  • Adults: ${adults} ${isGroup ? '(Group Tour)' : '(Solo)'}\n`;
      text += `  • Children: ${children}\n`;
      if (isMice) {
        text += `⚡ *Note:* Large group/MICE enquiry\n`;
      }
    } else if (activeTab === 'Flights') {
      text += `✈️ *Service Type:* Flights\n`;
      text += `📍 *From:* ${from || 'Not Specified'}\n`;
      text += `🗺️ *To:* ${to || 'Not Specified'}\n`;
      text += `📅 *Departure Date:* ${formattedDate}\n`;
      if (returnDate) {
        text += `📅 *Return Date:* ${formattedReturnDate}\n`;
      }
      text += `💺 *Class:* ${flightClass}\n`;
      text += `👥 *Travellers:*\n`;
      text += `  • Adults: ${adults}\n`;
      text += `  • Children: ${children}\n`;
    } else if (activeTab === 'Hotels') {
      text += `🏨 *Service Type:* Hotels\n`;
      text += `🗺️ *Destination:* ${to || 'Not Specified'}\n`;
      text += `📅 *Check-In:* ${formattedDate}\n`;
      if (returnDate) {
        text += `📅 *Check-Out:* ${formattedReturnDate}\n`;
      }
      text += `🛏️ *Rooms:* ${rooms}\n`;
      text += `👥 *Guests:*\n`;
      text += `  • Adults: ${adults}\n`;
      text += `  • Children: ${children}\n`;
    } else if (activeTab === 'Visa') {
      text += `🛂 *Service Type:* Visa Assistance\n`;
      text += `🗺️ *Destination Country:* ${to || 'Not Specified'}\n`;
      text += `📑 *Visa Type:* ${visaType}\n`;
      text += `📅 *Travel Date:* ${formattedDate}\n`;
      text += `👥 *Applicants:*\n`;
      text += `  • Adults: ${adults}\n`;
      text += `  • Children: ${children}\n`;
    } else if (activeTab === 'Corporate') {
      text += `🏢 *Service Type:* Corporate Travel\n`;
      text += `🏢 *Company Name:* ${company || 'Not Specified'}\n`;
      text += `👤 *Contact Person:* ${contactName || 'Not Specified'}\n`;
      text += `🗺️ *Destination:* ${to || 'Not Specified'}\n`;
      text += `📅 *Travel Date:* ${formattedDate}\n`;
      text += `👥 *Passengers:* ${passengers || 'Not Specified'}\n`;
    }

    text += `----------------------------------\n`;
    text += `Thank you for choosing Dira Holidays!`;

    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/917092210922?text=${encodedText}`, '_blank');
  };

  return (
    <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.22)', overflow: 'hidden', textAlign: 'left' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`search-tab ${activeTab === t ? 'active' : ''}`}
            style={{ whiteSpace: 'nowrap' }}
          >
            {t}
          </button>
        ))}
      </div>

      <div style={{ padding: '22px' }}>
        {/* Input row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '14px' }}>
          {activeTab === 'Holidays' && (
            <>
              <InputWrapper label="From"><input type="text" placeholder="City or Airport" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="To"><input type="text" placeholder="Where to?" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Travel Date"><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Category">
                <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
                  <option value="">Select Category</option>
                  {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </InputWrapper>
            </>
          )}

          {activeTab === 'Flights' && (
            <>
              <InputWrapper label="From"><input type="text" placeholder="City or Airport" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="To"><input type="text" placeholder="Where to?" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Departure Date"><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Return Date"><input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Class">
                <select value={flightClass} onChange={e => setFlightClass(e.target.value)} style={inputStyle}>
                  <option value="Economy">Economy</option>
                  <option value="Premium Economy">Premium Economy</option>
                  <option value="Business">Business</option>
                  <option value="First Class">First Class</option>
                </select>
              </InputWrapper>
            </>
          )}

          {activeTab === 'Hotels' && (
            <>
              <InputWrapper label="Destination"><input type="text" placeholder="City or Hotel Name" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Check-In"><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Check-Out"><input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Rooms"><input type="number" min="1" value={rooms} onChange={e => setRooms(e.target.value)} style={inputStyle} /></InputWrapper>
            </>
          )}

          {activeTab === 'Visa' && (
            <>
              <InputWrapper label="Destination Country"><input type="text" placeholder="Country" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Visa Type">
                <select value={visaType} onChange={e => setVisaType(e.target.value)} style={inputStyle}>
                  <option value="Tourist">Tourist</option>
                  <option value="Business">Business</option>
                  <option value="Student">Student</option>
                  <option value="Transit">Transit</option>
                  <option value="Other">Other</option>
                </select>
              </InputWrapper>
              <InputWrapper label="Travel Date"><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} /></InputWrapper>
            </>
          )}

          {activeTab === 'Corporate' && (
            <>
              <InputWrapper label="Company Name"><input type="text" placeholder="Company Name" value={company} onChange={e => setCompany(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Contact Person"><input type="text" placeholder="Your Name" value={contactName} onChange={e => setContactName(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Destination"><input type="text" placeholder="Where to?" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Travel Date"><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} /></InputWrapper>
              <InputWrapper label="Passengers"><input type="number" min="1" placeholder="Approx. Count" value={passengers} onChange={e => setPassengers(e.target.value)} style={inputStyle} /></InputWrapper>
            </>
          )}
        </div>

        {/* Travellers */}
        {activeTab !== 'Corporate' && (
          <div style={{ background: '#f8fafc', borderRadius: '9px', padding: '13px 18px', marginBottom: '14px', border: '1.5px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '2px', color: 'var(--brand)', textTransform: 'uppercase' }}>
              {activeTab === 'Visa' ? 'Applicants' : 'Travellers'}
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '13px', color: '#3d4a5c' }}>Adults</span>
              <Counter value={adults} onChange={setAdults} min={1} />
              {activeTab === 'Holidays' && isGroup ? <Pill bg="var(--brand)" color="#fff">Group Tour</Pill> : (activeTab === 'Holidays' ? <Pill bg="#f8fafc" color="#6b7a8f" small>Solo</Pill> : null)}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '13px', color: '#3d4a5c' }}>Children</span>
              <Counter value={children} onChange={setChildren} min={0} />
            </div>

            {activeTab === 'Holidays' && isMice && (
              <Pill bg="var(--sec-lt)" color="var(--sec-dk)">⚡ MICE team will contact you</Pill>
            )}
          </div>
        )}

        {/* Search button */}
        <button onClick={handleSearch} style={{ width: 'fit-content', margin: '0 auto', padding: '14px 28px', background: 'var(--secondary)', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 800, color: '#0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 8px 20px rgba(255,182,0,0.2)', cursor: 'pointer' }}>
          <Icon name="search" size={16} color="#0d1117" />
          {`${activeTab} Enquiry`}
        </button>
      </div>
    </div>
  );
}
